import { Link } from 'react-router-dom'

import { getStartPageUrl } from '@buffer-mono/app-shell/src/common/utils/urls'
import {
  DropdownMenu,
  ExternalLinkIcon,
  FacebookIcon,
  LogOutIcon,
  PencilIcon,
  RefreshCwIcon,
  SettingsIcon,
  TrashIcon,
  UserIcon,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact } from '@buffer-mono/tracking-plan'

import {
  useHasEntitlement,
  useHasOrganizationAction,
} from '~publish/contexts/AccountContext'
import {
  CopyIdMenuItem,
  OpenInHQMenuItem,
  useShouldShowDeveloperData,
} from '~publish/components/DeveloperData'
import { graphql } from '~publish/graphql'
import {
  AccountType,
  connectChannel,
} from '~publish/helpers/channelConnections'
import {
  getChannelName,
  isInstagramLoginChannel,
} from '~publish/helpers/channels'
import { navigateTo } from '~publish/helpers/navigation'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'

import DisconnectChannelAlertDialog from './DisconnectChannelAlertDialog'
import { isStartPage } from '../../shared/utils/isStartPage'
import type { ChannelMask } from '../hooks/useChannelsForSettings'
import { Status } from '../utils/getChannelStatus'
import { shouldShowRefreshButton } from '../utils/shouldShowRefreshButton'

import type { JSX } from 'react'

/**
 * Developer data menu items for channels.
 * Shows Copy Channel ID and Open in Retool options when developer data is enabled.
 */
const ChannelDeveloperDataItems = ({
  channelId,
}: {
  channelId: string
}): JSX.Element | null => {
  const shouldShowDeveloperData = useShouldShowDeveloperData()

  if (!shouldShowDeveloperData) {
    return null
  }

  return (
    <>
      <DropdownMenu.Separator />
      <DropdownMenu.Label>Developer Data</DropdownMenu.Label>
      <CopyIdMenuItem
        label="Copy Channel ID"
        toastMessage="Channel ID copied to clipboard!"
        id={channelId}
      />
      <OpenInHQMenuItem label="Open in HQ" type="channel" id={channelId} />
    </>
  )
}

const RefreshConnectionMenuItem = ({
  channel,
  refreshChannelConnection,
}: {
  channel: ChannelMask
  refreshChannelConnection: (cta: string) => void
}): JSX.Element => {
  const canManageChannels = useHasOrganizationAction('manageChannels')

  return (
    <DropdownMenu.Item
      onClick={(): void => {
        if (canManageChannels) {
          refreshChannelConnection(
            `publish-channels-menuItem-${channel.service.toLowerCase()}-1`,
          )
        }
      }}
      disabled={!canManageChannels}
    >
      <RefreshCwIcon />
      Refresh Connection
    </DropdownMenu.Item>
  )
}

export const RemoveChannelsMutation = graphql(/* GraphQL */ `
  mutation removeChannels($channelIds: [String!]) {
    channelsRemove(channelIds: $channelIds)
  }
`)

const StartPageMenuItems = ({
  channel,
  status,
}: {
  channel: ChannelMask
  status: Status
}): JSX.Element => {
  const canManageChannels = useHasOrganizationAction('manageChannels')
  return (
    <>
      <DropdownMenu.Item
        onClick={(): void => {
          if (canManageChannels) {
            navigateTo(getStartPageUrl(`${channel.id}`))
          }
        }}
        disabled={!canManageChannels}
      >
        <PencilIcon />
        Edit Start Page
      </DropdownMenu.Item>
      {status !== Status.Locked && (
        <DropdownMenu.Item
          onClick={(): void => {
            if (canManageChannels) {
              navigateTo(getStartPageUrl(`${channel.id}/settings`))
            }
          }}
          disabled={!canManageChannels}
        >
          <LogOutIcon />
          Unpublish Channel
        </DropdownMenu.Item>
      )}
    </>
  )
}

const FacebookGroupMenuItems = ({
  channel,
  onChannelUpdated,
}: {
  channel: ChannelMask
  onChannelUpdated?: () => void
}): JSX.Element => {
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const openGlobalModal = useOpenGlobalModal()
  return (
    <>
      <DropdownMenu.Item
        onClick={(): void => {
          if (canManageChannels) {
            openGlobalModal(MODALS.facebookSelectAccountType, {
              accountType: AccountType.group,
              isRefreshingConnection: true,
              channelId: channel.id,
              channelName: channel.name,
              channelUrl:
                channel.serverUrl ||
                (channel.metadata?.__typename === 'FacebookMetadata'
                  ? (channel.metadata.locationData?.location ?? undefined)
                  : undefined),
              channelAvatar: channel.avatar,
              onContinue: onChannelUpdated,
            })
          }
        }}
        disabled={!canManageChannels}
      >
        <PencilIcon />
        Edit Group
      </DropdownMenu.Item>
    </>
  )
}

const InstagramPersonalProfileMenuItems = ({
  channel,
}: {
  channel: ChannelMask
}): JSX.Element => {
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const openGlobalModal = useOpenGlobalModal()
  return (
    <>
      <DropdownMenu.Item
        onClick={(): void => {
          if (canManageChannels) {
            openGlobalModal(MODALS.instagramSelectAccountType, {
              accountType: AccountType.personal,
              isRefreshingConnection: true,
              channelId: channel.id,
              channelName: channel.name,
              channelAvatar: channel.avatar,
            })
          }
        }}
        disabled={!canManageChannels}
      >
        <PencilIcon />
        Edit Personal Account
      </DropdownMenu.Item>
      <DropdownMenu.Item
        onClick={(): void => {
          if (canManageChannels) {
            const { actions } = window?.appshell || {}
            if (!actions) return

            openGlobalModal(
              MODALS.instagramSwitchToProfessionalAccountWarning,
              {
                onContinue: () => {
                  BufferTrackerReact.channelConversionStarted({
                    channel: 'instagram',
                    clientName: 'publishWeb',
                    cta: 'account-channels-menuItem-switchToAProfessionalAccount-1',
                    organizationId: channel.organizationId,
                  })

                  connectChannel({
                    service: 'instagram',
                    customChannelIdToConvert: channel.id,
                    authMethod: 'instagramLogin',
                    cta: 'account-channels-menuItem-switchToAProfessionalAccount-1',
                  })
                },
              },
            )
          }
        }}
        disabled={!canManageChannels}
      >
        <RefreshCwIcon />
        Switch to Professional Account
      </DropdownMenu.Item>
    </>
  )
}

const InstagramLinkFacebookPageMenuItem = ({
  channel,
}: {
  channel: ChannelMask
}): JSX.Element => {
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const openGlobalModal = useOpenGlobalModal()

  return (
    <DropdownMenu.Item
      onClick={(): void => {
        if (canManageChannels) {
          openGlobalModal(MODALS.instagramFacebookReAuth, {
            channelId: channel.id,
            channelType: channel.type,
          })
        }
      }}
      disabled={!canManageChannels}
    >
      <FacebookIcon />
      Link Account to Facebook Page
    </DropdownMenu.Item>
  )
}

interface ChannelDropdownItemProps {
  channel: ChannelMask
  status: Status
  refreshChannelConnection: (cta: string) => void
  showSettingsLink: boolean
  onEditPermissions?: () => void
  onChannelUpdated?: () => void
}

const ChannelDropdownItems = ({
  channel,
  status,
  refreshChannelConnection,
  showSettingsLink,
  onEditPermissions,
  onChannelUpdated,
}: ChannelDropdownItemProps): JSX.Element => {
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const canManageTeamMembers = useHasOrganizationAction('manageTeamMembers')
  // The `canManageTeamMembers` privilege can linger after a Team trial ends
  // (members added during the trial remain on the account), so it isn't enough
  // to gate team features on its own. Also require the `teamsPanel` entitlement
  // so Team Access is hidden on plans without team access (e.g. Essentials).
  const hasTeamsPanelEntitlement = useHasEntitlement('teamsPanel')

  const isFacebookGroup =
    channel.service === 'facebook' && channel.type === 'group'
  const isInstagramPersonalProfile =
    channel.service === 'instagram' && channel.type === 'profile'
  const isChannelStartPage = isStartPage({ channel })
  const isInstagramLinkFacebookPageChannel = isInstagramLoginChannel(channel)
  const shouldShowExternalLink = !!channel.externalLink

  const disconnectChannel = canManageChannels ? (
    <DisconnectChannelAlertDialog
      channel={channel}
      handleRefreshChannelConnection={refreshChannelConnection}
    >
      <DropdownMenu.Item
        variant="critical"
        onSelect={(e): void => e.preventDefault()}
      >
        <TrashIcon />
        {isChannelStartPage ? 'Delete Start Page' : 'Disconnect Channel'}
      </DropdownMenu.Item>
    </DisconnectChannelAlertDialog>
  ) : (
    <DropdownMenu.Item onSelect={(e): void => e.preventDefault()} disabled>
      <TrashIcon />
      {isChannelStartPage ? 'Delete Start Page' : 'Disconnect Channel'}
    </DropdownMenu.Item>
  )

  const externalLink = (
    <DropdownMenu.Item asChild>
      <a
        href={channel.externalLink ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
      >
        <ExternalLinkIcon />
        {isChannelStartPage
          ? 'View Start Page'
          : `View on ${getChannelName(channel.service)}`}
      </a>
    </DropdownMenu.Item>
  )

  const shouldOnlyShowDisconnect =
    status === Status.Locked &&
    !isChannelStartPage &&
    !isFacebookGroup &&
    !isInstagramPersonalProfile

  // Special cases for locked channels with only a disconnect option
  if (shouldOnlyShowDisconnect) {
    if (shouldShowExternalLink) {
      return (
        <>
          {externalLink}
          <DropdownMenu.Separator />
          {disconnectChannel}
          <ChannelDeveloperDataItems channelId={channel.id} />
        </>
      )
    }
    return (
      <>
        {disconnectChannel}
        <ChannelDeveloperDataItems channelId={channel.id} />
      </>
    )
  }

  return (
    <>
      {shouldShowExternalLink && externalLink}
      {showSettingsLink && (
        <DropdownMenu.Item asChild>
          <Link to={`/channels/${channel.id}/settings`}>
            <SettingsIcon />
            Channel Settings
          </Link>
        </DropdownMenu.Item>
      )}
      {isChannelStartPage && (
        <StartPageMenuItems channel={channel} status={status} />
      )}
      {isFacebookGroup && (
        <FacebookGroupMenuItems
          channel={channel}
          onChannelUpdated={onChannelUpdated}
        />
      )}
      {isInstagramPersonalProfile && (
        <InstagramPersonalProfileMenuItems channel={channel} />
      )}
      {isInstagramLinkFacebookPageChannel && (
        <InstagramLinkFacebookPageMenuItem channel={channel} />
      )}
      {shouldShowRefreshButton({
        channel: {
          id: channel.id,
          service: channel.service,
          type: channel.type,
        },
        status,
      }) && (
        <RefreshConnectionMenuItem
          channel={channel}
          refreshChannelConnection={refreshChannelConnection}
        />
      )}
      {canManageTeamMembers &&
        hasTeamsPanelEntitlement &&
        onEditPermissions && (
          <DropdownMenu.Item onClick={onEditPermissions}>
            <UserIcon />
            Team Access
          </DropdownMenu.Item>
        )}
      <DropdownMenu.Separator />
      {disconnectChannel}
      <ChannelDeveloperDataItems channelId={channel.id} />
    </>
  )
}

export default ChannelDropdownItems
